// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Button, Box, Image, Grid, Text, Switch, Spinner, useDisclosure, Input, Textarea } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { IoLocationSharp, IoClose, IoPeople } from 'react-icons/io5';
import { TbRainbow } from 'react-icons/tb';
import { MdWork } from 'react-icons/md';
import { FaGenderless, FaRuler, FaAmericanSignLanguageInterpreting, FaReact } from 'react-icons/fa';
import { MdOutlineInterests } from 'react-icons/md';
import { TbEdit } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa6';
import { GiGemini, GiAries, GiTaurus, GiCancer, GiLeo, 
  GiVirgo, GiLibra, GiScorpio, GiSagittarius, GiCapricorn, 
  GiAquarius, GiPisces } from 'react-icons/gi';
import { SiGotomeeting } from 'react-icons/si';
import { MdEmojiPeople } from 'react-icons/md';
import { MdVerified } from 'react-icons/md';
import { useQuery } from 'react-query';
import { LiaBirthdayCakeSolid } from 'react-icons/lia';

// Assets
import { getAge, parseDateBirthday } from 'services/Utils';
import { textLang } from 'services/Lang';

// Components
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';
import ErrorCard from 'components/Form/Errors';

const ProfileIFind = ({
	object, 
	setObject,
	handleUpdateObject,
	handleField,
	findAgeFrom,
	findAgeTo,
	setFindAgeFrom,
	setFindAgeTo
}) => {
	const textColor = '#0E1633';

	const [editIFind, setEditIFind] = useState(false);

	const handleSetFindAgeFrom = (e) => { 
		let from = e.target.value;
		const to = findAgeTo;
		if(from > to){
			from = to;
		}

		setFindAgeFrom(from);
		setObject({...object, findAge: [from, findAgeTo]}); 
	};

	const handleSetFindAgeTo = (e) => { 
		let to = e.target.value;
		const from = findAgeFrom;
		if(to < from){
			to = from;
		}

		setFindAgeTo(to);
		setObject({...object, findAge: [findAgeFrom, to]}); 
	};

	return (
	<Box bg="white" mt="16px" borderRadius="16px">
		<Flex alignItems="top" justifyContent="space-between" mb="12px">
			<Text variant="text-md" px="4px" w="80%">{textLang('i_find_header')}</Text>

			{ !editIFind ?
					<TbEdit onClick={() => setEditIFind(!editIFind)} color={textColor} fontSize="36px" />
					:
					<Flex overflow="visible !important" gap="16px" alignItems="top">
						<Box mt="-2px">
							<IoClose color={textColor} onClick={() => {
								setEditIFind(!editIFind);
							}} fontSize="34px" /> 
						</Box>

						<Box overflow="visible !important">
							<FaCheck color={textColor} onClick={() => {
								setEditIFind(!editIFind);
								handleUpdateObject();
							}} fontSize="30px"/> 
						</Box>
					</Flex>
			}
		</Flex>

		{ !editIFind ? 
			<>
				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
						<Flex alignItems="center" gap="8px">
							<MdOutlineInterests color={textColor} fontSize="21px" /> 
							<Text>{object?.findGender === 'Unknown' ? textLang('gender_other') : textLang(object?.findGender)}</Text>
						</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<FaAmericanSignLanguageInterpreting color={textColor} fontSize="21px" /> 
						{Array.isArray(object?.findAge) && object?.findAge[0] && object?.findAge[1] ? (
				      <Text>{textLang('placeholder_before')} {object.findAge[0]} {textLang('placeholder_to')} {object.findAge[1]}</Text>
				    ) : (
				      <Text opacity="0.6">{textLang('placeholder_age')}</Text>
				    )}
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center">
					<Flex alignItems="center" gap="8px">
						<SiGotomeeting color={textColor} fontSize="21px" /> 
						<Text>{textLang(object?.findFor)}</Text>
					</Flex>
				</Flex>
			</>
			:
			<Box>
				<EditSelect 
					mb="-12px"
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Male', title: textLang('Male') },
        		{ value: 'Female', title: textLang('Female') },
        		{ value: 'Transgender', title: textLang('Transgender') },
        		{ value: 'Unknown', title: textLang('gender_other') },
        	]}
        	id="findGender" value={object?.findGender} onChange={handleField?.findGender} isValid={true}  />

        <Flex gap="12px" mb="-12px">
        	<EditSelect 
	        	editMode={true}
	        	bg="#f1f1f1 !important"
	        	options={Array.from({ length: 118 }, (_, index) => ({
					    value: 18 + index,
					    title: `${18 + index}`
					  }))}
	        	id="findFor" value={findAgeFrom} onChange={handleSetFindAgeFrom} isValid={true}  />

        	<EditSelect 
	        	editMode={true}
	        	bg="#f1f1f1 !important"
	        	options={Array.from({ length: 118 }, (_, index) => ({
					    value: 18 + index,
					    title: `${18 + index}`
					  }))}
	        	id="findFor" value={findAgeTo} onChange={handleSetFindAgeTo} isValid={true}  />
        </Flex>

        <EditSelect 
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Serious relationship', title: textLang('Serious relationship') },
        		{ value: 'Casual dating', title: textLang('Casual dating') },
        		{ value: 'Friendship', title: textLang('Friendship') },
        		{ value: 'Hangouts', title: textLang('Hangouts') },
        		{ value: 'Networking', title: textLang('Networking') },
        		{ value: 'Fun & No commitments', title: textLang('Fun & No commitments') },
        	]}
        	id="findFor" value={object?.findFor} onChange={handleField?.findFor} isValid={true}  />
			</Box>
		}
	</Box>
	);
};

export default ProfileIFind;